import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { CharacterLimitPlugin } from "@lexical/react/LexicalCharacterLimitPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import LexicalClickableLinkPlugin from "@lexical/react/LexicalClickableLinkPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HashtagPlugin } from "@lexical/react/LexicalHashtagPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import useLexicalEditable from "@lexical/react/useLexicalEditable";
import * as React from "react";
import { MouseEventHandler, useEffect, useState } from "react";
import { $generateHtmlFromNodes } from "@lexical/html";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Box, Typography } from "@mui/material";
import { verisColors, VrsButton } from "@veris-health/web-core";
import { Lang } from "@veris-health/virtual-doc-ms/lib/v1";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { CAN_USE_DOM } from "./shared/canUseDOM";
import { useSettings } from "./context/SettingsContext";
import { useSharedHistoryContext } from "./context/SharedHistoryContext";
import AutoEmbedPlugin from "./plugins/AutoEmbedPlugin";
import AutoLinkPlugin from "./plugins/AutoLinkPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import MarkdownShortcutPlugin from "./plugins/MarkdownShortcutPlugin";
import CollapsiblePlugin from "./plugins/CollapsiblePlugin";
import ComponentPickerPlugin from "./plugins/ComponentPickerPlugin";
import DragDropPaste from "./plugins/DragDropPastePlugin";
import DraggableBlockPlugin from "./plugins/DraggableBlockPlugin";
import EmojisPlugin from "./plugins/EmojisPlugin";
import FloatingLinkEditorPlugin from "./plugins/FloatingLinkEditorPlugin";
import ImagesPlugin from "./plugins/ImagesPlugin";
import InlineImagePlugin from "./plugins/InlineImagePlugin";
import LinkPlugin from "./plugins/LinkPlugin";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import { MaxLengthPlugin } from "./plugins/MaxLengthPlugin";
import TabFocusPlugin from "./plugins/TabFocusPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import ContentEditable from "./ui/ContentEditable";
import Placeholder from "./ui/Placeholder";
import { TitleInput } from "./ui/Title";
import ActionsPlugin from "./plugins/ActionsPlugin";

interface EditorProps {
  title?: string;
  setTitle: (title: string) => void;
  onChange: (htmlString: string, lang: Lang) => void;
  onSave: () => void;
  lang?: Lang;
}

export default function Editor({
  title = "",
  setTitle,
  lang = Lang.En,
  onSave,
  onChange,
}: EditorProps): JSX.Element {
  const { historyState } = useSharedHistoryContext();
  const [editor] = useLexicalComposerContext();
  const {
    settings: { isMaxLength, isCharLimit, isRichText },
  } = useSettings();
  const isEditable = useLexicalEditable();
  const text = "Start typing...";

  const placeholder = <Placeholder>{text}</Placeholder>;
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] = useState<boolean>(false);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia("(max-width: 1025px)").matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener("resize", updateViewPortWidth);

    return () => {
      window.removeEventListener("resize", updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "1rem" }}>
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <Box sx={{ display: "flex" }}>
            <TitleInput
              autoFocus
              placeholder="Write your title"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.value.length < 61) {
                  setTitle(e.target.value);
                }
              }}
              onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
            />
            <VrsButton
              disabled={title === "" || editor.getEditorState().isEmpty()}
              buttonType="primary"
              size="small"
              onClick={() => onSave()}
            >
              Save
            </VrsButton>
          </Box>
          <Box sx={{ marginBottom: "2rem" }}>
            <Typography
              variant="body"
              sx={{
                fontWeight: "400",
                color: verisColors.neutrals["grey-3"],
              }}
            >
              {title.length}/60 Characters
            </Typography>
          </Box>
        </Box>
      </Box>
      {isRichText && <ToolbarPlugin />}
      <div className={`editor-container  ${!isRichText ? "plain-text" : ""}`}>
        {isMaxLength && <MaxLengthPlugin maxLength={30} />}
        <DragDropPaste />
        <AutoFocusPlugin />
        <ClearEditorPlugin />
        <ComponentPickerPlugin />
        <AutoEmbedPlugin />
        <EmojisPlugin />
        <HashtagPlugin />
        <AutoLinkPlugin />
        <HistoryPlugin externalHistoryState={historyState} />
        <RichTextPlugin
          contentEditable={
            <div className="editor-scroller">
              <div className="editor" ref={onRef}>
                <ContentEditable />
              </div>
            </div>
          }
          placeholder={placeholder}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <MarkdownShortcutPlugin />
        <CodeHighlightPlugin />
        <ListPlugin />
        <CheckListPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <ImagesPlugin />
        <InlineImagePlugin />
        <LinkPlugin />
        {!isEditable && <LexicalClickableLinkPlugin />}
        <HorizontalRulePlugin />
        <TabFocusPlugin />
        <TabIndentationPlugin />
        <CollapsiblePlugin />
        {floatingAnchorElem && !isSmallWidthViewport && (
          <>
            <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
            <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem} />
          </>
        )}
        {isCharLimit && (
          <CharacterLimitPlugin charset={isCharLimit ? "UTF-16" : "UTF-8"} maxLength={5} />
        )}
        <ActionsPlugin />
        <OnChangePlugin
          onChange={(editorState, lexicalEditor) => {
            lexicalEditor.update(async () => {
              const htmlString = $generateHtmlFromNodes(editor, null);
              onChange(htmlString, lang);
            });
          }}
        />
      </div>
    </>
  );
}
